'use client';

import React, { useState } from 'react';
import { ShieldCheck, Mail, Lock, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import { createClient } from '../../../../lib/supabase/client';

const SUPER_ADMIN_EMAIL = 'superadmin@sellnex.net';

export default function SuperLoginContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setError('');

    const supabase = createClient();
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (signInError || !data.session) {
      const msg = signInError?.message?.toLowerCase() ?? '';
      let friendly = signInError?.message || 'Invalid credentials.';
      if (msg.includes('invalid login')) {
        friendly = 'Invalid email or password. Please check your credentials or contact support.';
      } else if (msg.includes('email not confirmed')) {
        friendly = 'Email not confirmed. The Super Admin account should be pre-confirmed — try re-running migrations.';
      }
      setError(friendly);
      setLoading(false);
      return;
    }

    const user = data.session.user;
    const metaRole = (user.app_metadata?.role as string | undefined)?.toUpperCase();
    const isSuperAdmin =
      user.email === SUPER_ADMIN_EMAIL ||
      metaRole === 'SUPER_ADMIN';

    if (!isSuperAdmin) {
      await supabase.auth.signOut();
      setError('Access denied. Super Admin credentials required.');
      setLoading(false);
      return;
    }

    // Persist session tokens so the super-admin layout can restore them after the full-page redirect
    try {
      sessionStorage.setItem(
        'sb-login-session',
        JSON.stringify({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        })
      );
    } catch {
      // ignore storage errors
    }

    window.location.href = '/super-admin/dashboard';
  };

  return (
    <div className="min-h-screen flex bg-[#0a0f1e]">
      {/* Left decorative panel */}
      <div className="hidden lg:flex lg:w-[45%] relative flex-col justify-between p-12 overflow-hidden bg-gradient-to-br from-[#0d1b35] to-[#0a0f1e] border-r border-white/5">
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
            <ShieldCheck size={20} className="text-amber-400" />
          </div>
          <div>
            <p className="text-white font-bold text-lg leading-none">CourtBook</p>
            <p className="text-amber-400/80 text-xs font-500 tracking-widest uppercase">Super Admin</p>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div>
            <h1 className="text-white text-3xl font-700 leading-tight mb-3">
              Platform Control<br />
              <span className="text-amber-400">Center</span>
            </h1>
            <p className="text-white/50 text-sm leading-relaxed">
              Manage all businesses, subscriptions, users, and system settings from one unified panel.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { label: 'Business Management', desc: 'Create, suspend, and manage all tenants' },
              { label: 'Subscription Control', desc: 'Upgrade, downgrade, and extend plans' },
              { label: 'Global Monitoring', desc: 'Bookings, payments, and system health' },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/5">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-600">{item.label}</p>
                  <p className="text-white/40 text-xs mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 text-white/30 text-xs">
            <ShieldCheck size={12} />
            <span>Restricted access — authorized personnel only</span>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
              <ShieldCheck size={20} className="text-amber-400" />
            </div>
            <div>
              <p className="text-white font-bold text-lg leading-none">CourtBook</p>
              <p className="text-amber-400/80 text-xs font-500 tracking-widest uppercase">Super Admin</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-white text-2xl font-700 mb-1">Super Admin Login</h2>
            <p className="text-white/40 text-sm">Enter your credentials to access the control panel</p>
          </div>

          {error && (
            <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-red-500/10 border border-red-500/20 mb-6">
              <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-500 text-white/60 mb-1.5">Email Address</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={SUPER_ADMIN_EMAIL}
                  autoComplete="username"
                  className="w-full pl-10 pr-4 py-3 text-sm rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/40 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-500 text-white/60 mb-1.5">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full pl-10 pr-10 py-3 text-sm rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/40 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-black font-600 text-sm transition-colors mt-2"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Authenticating…
                </>
              ) : (
                <>
                  <ShieldCheck size={16} />
                  Access Control Panel
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
